import { forwardRef, Inject, Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { isOnline, LOCALLY_HOSTED } from '../../common/setup';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { CustomLabelService } from '../../service/custom-label.service';
import { CustomPropertyService } from '../../service/custom-property.service';
import { DigitalServiceService } from '../../service/digital-service.service';
import { ExternalAppUtilsService } from '../../service/external-app-utils.service';
import { LocalizationService } from '../../service/localization.service';
import { NavigationService } from '../../service/navigation.service';
import { OfflineNavigationService } from '../../service/offline-navigation.service';
import { UiService } from '../../service/ui.service';

@Injectable()
export class DashboardGuard {

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => CustomLabelService)) private customLabelService: CustomLabelService,
        @Inject(forwardRef(() => AppService)) private appService: AppService,
        @Inject(forwardRef(() => Router)) private router: Router,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => UiService)) private uiService: UiService,
        @Inject(forwardRef(() => ExternalAppUtilsService)) private externalAppUtilsService: ExternalAppUtilsService,
        @Inject(forwardRef(() => LocalizationService)) private localizationService: LocalizationService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => OfflineNavigationService)) private offlineNavigationService: OfflineNavigationService,
        @Inject(forwardRef(() => DigitalServiceService)) private digitalServiceService: DigitalServiceService
    ) { }


    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        this.appService.parseQueryString(route);
        return this.authenticationService.me()
            .then(user => {
                if (user) {
                    if (user.selfRegisteredWithPendingCustomer) {
                        let url = state.url.split('?')[0];
                        this.navigationService.setPreLoginUrl(url);
                        this.router.navigateByUrl('/create-new-account/selfRegisteredWithPendingCustomer');
                        return false;
                    } else {
                        this.zone.runOutsideAngular(() => {
                            this.localizationService.init();
                            this.customLabelService.load();
                            this.uiService.load();
                            this.customPropertyService.loadCustomPropertyDefinitions();
                            this.externalAppUtilsService.init();
                            this.digitalServiceService.loadDigitalServices();
                            this.contextService.initTags();
                            if (LOCALLY_HOSTED && isOnline()) {
                                this.uiService.waitForLoaded().then(() =>
                                    this.offlineNavigationService.synchronizeOfflineRequests()
                                        .then(() => this.offlineNavigationService.storeGeneralData()));
                            }
                        });
                        return true;
                    }
                } else {
                    // only carry what a report page reads across login (its mode and period), drop apiKey/mobile/etc.
                    const carried = new URLSearchParams();
                    for (const name of ['mode', 'inputStartTimestamp', 'inputEndTimestamp']) {
                        if (route.queryParams[name]) {
                            carried.set(name, route.queryParams[name]);
                        }
                    }
                    let url = state.url.split('?')[0];
                    if (carried.toString()) {
                        url += '?' + carried.toString();
                    }
                    this.navigationService.setPreLoginUrl(url);
                    return this.router.navigate(['/login'], { queryParams: route.queryParams });
                }
            });
    }

}